import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

interface RequestDetails {
  productId: string;
  search: string;
  page: number;
  record: number;
  sortOrder: string;
  sortColumn: string;
  exclude?: string;
  skuRelation?: boolean;
}
interface SKU {
  SkuRelation: SKU[] | undefined;
  SkuId: string;
  Name: string;
  Description: string;
  StatusCd: string;
  CreatedDtm: string;
  UpdatedDtm: string;
  CreatedUser: string;
  ValidFrom: string;
  ValidTo: string;
}

interface SkuListState {
  isLoading: boolean;
  data: {
    page: number;
    record: number;
    totalRecord: number;
    Result: SKU[];
  } | null;
  error: string | null;
}

const initialState: SkuListState = {
  isLoading: true,
  data: {
    page: 1,
    record: 0,
    totalRecord: 0,
    Result: [],
  },
  error: null,
};

const mapSortColumn = (sortColumn: string) => {
  if (sortColumn === "SKU #") {
    sortColumn = "SkuId";
  } else if (sortColumn === "SKU Name") {
    sortColumn = "Name";
  } else if (sortColumn === "Created By") {
    sortColumn = "CreatedUser";
  } else if (sortColumn === "Created Date") {
    sortColumn = "CreatedDtm";
  } else if (sortColumn === "Updated Date") {
    sortColumn = "UpdatedDtm";
  } else if (sortColumn === "Status") {
    sortColumn = "StatusCd";
  } else if (sortColumn === "S.No.") {
    sortColumn = "serialNo";
  } else {
    sortColumn = "";
  }
  return sortColumn;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchSkuListAllData = createAsyncThunk<any, RequestDetails>(
  "CTDBSkuListAllSlice/fetchSkuListAllData",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/db/product/sku", {
        ProductId: requestDetails.productId,
        Search: requestDetails.search,
        Page: requestDetails.page,
        Record: requestDetails.record,
        SortOrder: requestDetails.sortOrder.toUpperCase() || "ASC",
        SortColumn: mapSortColumn(requestDetails.sortColumn) || "SkuId",
        Action: "retrieveAll",
        Exclude: requestDetails.exclude || "",
        skuRelation: requestDetails.skuRelation || false,
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to get product sku list",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get product sku list",
      });
    }
  }
);

const CTDBSkuListAllSlice = createSlice({
  name: "CTDBSkuListAllSlice",
  initialState,
  reducers: {
    resetSkuListAllState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSkuListAllData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchSkuListAllData.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        }
      )
      .addCase(
        fetchSkuListAllData.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message;
        }
      );
  },
});

export const { resetSkuListAllState } = CTDBSkuListAllSlice.actions;

export default CTDBSkuListAllSlice.reducer;
